// JS mirror of tokens.css, for the inline-style call sites that stay inline
// (behaviour-coupled styling: per-row grid cells, chart geometry, computed states).
//
// Keep in sync with tokens.css — same values, camelCase keys. Prefer the CSS
// classes in orbit.css wherever the styling is static; reach for this only when
// a value has to be computed in JS.

export const T = {
  // Ink — primary actions, the top bar, the preview strip, the code editor pane.
  ink: '#171A1F',
  inkLift: '#22262E',
  inkLine: '#31363F',

  // Paper — page and surface backgrounds.
  paper: '#F5F5F2',
  surface: '#FFFFFF',
  surface2: '#FAFAF8',

  // Rules — borders and dividers.
  rule: '#E3E2DD',
  ruleSoft: '#EEEDE9',
  ruleHover: '#C9C7C0',

  // Text.
  text: '#171A1F',
  text2: '#5B6472',
  text3: '#8C94A0',
  textInvert: '#F5F5F2',

  // Brass — RATIONED. Active underlines, focus rings, mono numerals, brand mark.
  // Never a button fill, never a large area.
  brass: '#8C6A2F',
  brassLift: '#C9A227',
  brassWash: '#F5EEDF',

  // Code green — taxonomy codes and data emphasis. Not an accent colour.
  code: '#2F5D50',
  codeWash: '#E7EFEC',

  // Status.
  valid: '#2E6B4F',
  validWash: '#E4F0E9',
  warn: '#A4560E',
  warnWash: '#F6EADD',
  error: '#A32B2B',
  errorWash: '#F5E4E4',

  // Extended categorical swatches. These colour user-authored data; they are never UI accents.
  dataRedBright: '#FF1111',
  dataGreenLight: '#C6EFCE',
  dataOrangeLight: '#EC804A',
  dataBrownLight: '#C9A35F',
  dataPurpleDark: '#5E5EA0',
  dataBlueDark: '#0338AD',
  dataPinkDark: '#B3278E',
  dataYellowLight: '#FFEB9C',
  dataRedLight: '#FFABB5',

  // Type. Display = page titles + top-bar wordmark only. Mono = data only.
  fontDisplay: "'Instrument Sans',-apple-system,sans-serif",
  fontUi: "'Public Sans',-apple-system,sans-serif",
  fontMono: "'IBM Plex Mono',ui-monospace,monospace",

  tDisplay: '1.875rem',
  tBody: '.9375rem',
  tSmall: '.8125rem',
  tLabel: '.6875rem',

  // Spacing scale (numbers — React appends px).
  s1: 4, s2: 8, s3: 12, s4: 16, s5: 24, s6: 32, s7: 48,

  // Radius scale: xs = inner nested elements, sm = controls/chips,
  // md = panels/cards, lg = overlays (modals), full = capsules.
  rXs: 6,
  rSm: 8,
  rMd: 12,
  rLg: 16,
  rFull: 999,

  // Elevation — three levels only. shadowModal is an alias kept for old call
  // sites; new code says shadowLg.
  shadowSm: '0 1px 2px rgba(23,26,31,.05)',
  shadow: '0 1px 2px rgba(23,26,31,.05),0 2px 8px rgba(23,26,31,.05),0 8px 24px rgba(23,26,31,.05)',
  shadowLg: '0 2px 6px rgba(23,26,31,.07),0 12px 32px rgba(23,26,31,.12),0 32px 64px rgba(23,26,31,.08)',
  shadowModal: '0 2px 6px rgba(23,26,31,.07),0 12px 32px rgba(23,26,31,.12),0 32px 64px rgba(23,26,31,.08)',

  // Focus ring — the brass glow every focused control wears.
  ring: '0 0 0 3px rgba(140,106,47,.14)',

  // Motion.
  tHover: '.15s',
  tReveal: '.22s cubic-bezier(.2,.7,.3,1)',
  ease: 'cubic-bezier(.2,.7,.3,1)',
};

// Chart series palette — the reference's own KEYC array (the colours it uses to
// underline values in the preview strip). Muted by design; they must not compete
// with brass.
export const SERIES = ['#C9A227', '#7FB8A6', '#9A9AC4', '#C99AAB', '#93AFCF', '#A8B0BC'];

// ── Small style-object helpers, mirroring orbit.css for inline call sites ──

export const eyebrow = (extra = {}) => ({
  fontSize: T.tLabel, fontWeight: 600, letterSpacing: '.09em',
  textTransform: 'uppercase', color: T.text3, margin: 0, ...extra,
});

export const title = (extra = {}) => ({
  fontFamily: T.fontDisplay, fontSize: T.tDisplay, fontWeight: 600,
  letterSpacing: '-.022em', lineHeight: 1.15, margin: 0, ...extra,
});

export const panel = (extra = {}) => ({
  background: T.surface, border: `1px solid ${T.rule}`,
  borderRadius: T.rMd, boxShadow: T.shadow, ...extra,
});

export const control = (extra = {}) => ({
  width: '100%', padding: '9px 11px', border: `1px solid ${T.rule}`,
  borderRadius: T.rSm, background: T.surface, fontSize: T.tBody,
  appearance: 'none', outline: 'none', ...extra,
});

// variant: 'default' | 'primary' | 'ghost' | 'danger'; size: 'md' | 'sm'
export const btn = (variant = 'default', extra = {}, size = 'md') => {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: size === 'sm' ? '4px 10px' : '7px 13px',
    borderRadius: T.rSm,
    fontSize: size === 'sm' ? '.75rem' : T.tSmall,
    fontWeight: size === 'sm' ? 500 : 600,
    fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1px solid ${T.rule}`, background: T.surface, color: T.text,
  };
  const variants = {
    default: {},
    primary: { background: T.ink, borderColor: T.ink, color: T.textInvert },
    ghost:   { background: 'transparent', borderColor: 'transparent', color: T.text2 },
    danger:  { background: T.error, borderColor: T.error, color: '#FFF' },
  };
  return { ...base, ...(variants[variant] || {}), ...extra };
};

export const badge = (tone = 'brass', extra = {}) => {
  const tones = {
    brass: { background: T.brassWash, color: T.brass },
    grey:  { background: '#EDECE8', color: T.text2 },
    ok:    { background: T.validWash, color: T.valid },
    err:   { background: T.errorWash, color: T.error },
  };
  return {
    fontSize: 10, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase',
    padding: '2px 8px', borderRadius: T.rFull, display: 'inline-block',
    ...(tones[tone] || tones.brass), ...extra,
  };
};

// Mono code chip. `tone: 'ink'` = white-on-ink, `'teal'` = the green code wash.
export const code = (tone = 'ink', extra = {}) => ({
  fontFamily: T.fontMono, fontSize: T.tLabel, fontWeight: 600, letterSpacing: '.03em',
  padding: '3px 6px', borderRadius: T.rXs,
  ...(tone === 'teal'
    ? { background: T.codeWash, color: T.code }
    : { background: T.ink, color: '#FFF' }),
  ...extra,
});

export const th = (extra = {}) => ({
  textAlign: 'left', fontSize: T.tLabel, fontWeight: 600, letterSpacing: '.09em',
  textTransform: 'uppercase', color: T.text3, padding: `11px ${T.s4}px`,
  borderBottom: `1px solid ${T.rule}`, whiteSpace: 'nowrap', background: T.surface2, ...extra,
});

export const td = (extra = {}) => ({
  padding: `11px ${T.s4}px`, borderBottom: `1px solid ${T.ruleSoft}`,
  verticalAlign: 'middle', fontSize: T.tSmall, color: T.text, ...extra,
});
